import pluralize from 'pluralize'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Tag } from 'antd'
import { useGetOboDetails } from '@Admin/AdminQuotingPage/components/PartDFMReportPanel/logic'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'
import { MANUFACTURABILITY_FEEDBACK_TAB } from '@Orders/constants'
import { selectFlags } from '@Quotes/selectors'

import Box from '~/core/Box'
import Link from '~/core/Link'
import FileName from '~/core/FileName'
import TruncatedText from '~/core/TruncatedText'
import { ADMIN_PATH, ORDERS_PATH } from '~/redux-modules/preferences'
import {
    TOOLING_PROCESSES,
    TOOLING_PROCESSES_IDS,
    TOOLING_PROCESSES_SAMPLES_ID,
} from '~/lib/partsConstants'
import formatRevisionVersion from '~/lib/formatRevisionVersion'
import getColor from '~/lib/getColor'
import { useFlagEnabled } from '~/lib/isFlagEnabled'
import SafeDownloadLink from '~/apps/consumer/components/SafeDownloadLink'
import { DFM_FEEDBACK_KEY } from '~/apps/consumer/components/PartViewerModal/logic'
import styles from '../styles.css'

import { DEFAULT_TEXT_LENGTH, MAX_FILE_NAME_LENGTH, MAX_LENGTH } from './logic'
import { DataTableFileNameType } from './types'

const DataTableFileName = (props: DataTableFileNameType) => {
    const {
        associatedParts,
        revision,
        twoDdrawing,
        processes,
        isOrderedStatusTable,
        isImOrder,
        isEnabledForIm,
        dfmReports,
        dfmFeedback,
        placement,
        erpMoldId,
        showMoldTag,
        itemsId,
        isOrderDetailPage,
        orderName,
        qmrId,
        quoteUserId,
        setSelectedTab,
    } = props

    const navigate = useNavigate()

    const isImToolingEnabled = useFlagEnabled(
        PROVISIONED_UI_FLAGS.IM_TOOLING_UI,
    )
    const { isImMaafcoEnabled } = useSelector(selectFlags)

    const formattedVersion = formatRevisionVersion(revision?.version)
    const twoDdrawingFileName =
        twoDdrawing?.annotated?.fileName || twoDdrawing?.fileName
    const twoDdrawingDownloadUrl =
        twoDdrawing?.annotated?.downloadUrl || twoDdrawing?.downloadUrl
    const isTooling = processes.some(
        ({ platformProcessId }) =>
            platformProcessId &&
            TOOLING_PROCESSES_IDS?.indexOf(platformProcessId) >= 0,
    )
    const isSamples = processes.some(
        ({ platformProcessId }) =>
            platformProcessId &&
            TOOLING_PROCESSES_SAMPLES_ID?.indexOf(platformProcessId) >= 0,
    )
    const isProcessTooling = processes.some(
        ({ platformProcessId }) =>
            platformProcessId &&
            TOOLING_PROCESSES?.indexOf(platformProcessId) >= 0,
    )
    const { isObo, oboUserId } = useGetOboDetails(quoteUserId || '')
    const seeDfmReportsUrl = isObo
        ? `${ADMIN_PATH}/user/${oboUserId}${ORDERS_PATH}/${orderName}/part/${qmrId}/${DFM_FEEDBACK_KEY}`
        : `${ORDERS_PATH}/${orderName}/part/${qmrId}/${DFM_FEEDBACK_KEY}`
    const seeDfmFeedbackUrl = isObo
        ? `${ADMIN_PATH}/user/${oboUserId}${ORDERS_PATH}/${orderName}#${MANUFACTURABILITY_FEEDBACK_TAB}`
        : `${ORDERS_PATH}/${orderName}#${MANUFACTURABILITY_FEEDBACK_TAB}`

    const onRevisionFileNamelinkClick = (evt: Event) => {
        evt.preventDefault()
        if (!isSamples && qmrId && isObo) {
            evt.stopPropagation()
            navigate(`/admin/quote-model-revisions/${qmrId}`)
        }
    }

    const onSeeDFMReportLinkClick = (evt: Event) => {
        evt.preventDefault()
        if (qmrId) {
            evt.stopPropagation()
            navigate(seeDfmReportsUrl)
        }
    }

    const onDFMFeedbacklinkClick = (evt: Event) => {
        evt.preventDefault()
        evt.stopPropagation()
        if (setSelectedTab) {
            setSelectedTab(MANUFACTURABILITY_FEEDBACK_TAB)
        }
        navigate(seeDfmFeedbackUrl, { replace: true })
    }

    const dfmReportsCount =
        dfmReports?.filter(report => report.isPublished)?.length || 0
    const hasDfmReports = Boolean(dfmReportsCount) && isEnabledForIm
    const dfmFeedbackCount =
        dfmFeedback?.filter(feedback => feedback.isPublished)?.length || 0
    const hasDfmFeedback = Boolean(dfmFeedbackCount) && isEnabledForIm

    const hasUnpublishedReports = dfmReportsCount !== dfmReports?.length
    const hasUnpublishedFeedbacks = dfmFeedbackCount !== dfmFeedback?.length
    const showMoldIdTag =
        isImToolingEnabled && isImOrder && showMoldTag && isOrderDetailPage
    const showTsampleMsg = isSamples && isOrderDetailPage && !isImToolingEnabled
    const showUnReleasedDfmTag =
        (!isImToolingEnabled &&
            isObo &&
            hasUnpublishedReports &&
            isOrderedStatusTable) ||
        (isProcessTooling && hasUnpublishedFeedbacks)

    const renderAssociatedParts = () => {
        return associatedParts?.map((part: any) => {
            return (
                <Tag
                    className={styles.moldTag}
                    style={{ marginBottom: '5px' }}
                    key={part}
                >
                    <TruncatedText
                        maxLength={
                            isOrderedStatusTable
                                ? MAX_LENGTH
                                : DEFAULT_TEXT_LENGTH
                        }
                    >
                        {part}
                    </TruncatedText>
                </Tag>
            )
        })
    }

    const moldNameForTooling = () => {
        if (isOrderDetailPage && isImToolingEnabled) {
            return (
                <Box>
                    <span>{erpMoldId || `mold-${itemsId}`}</span>
                </Box>
            )
        } else {
            return (
                <Box direction="col">
                    <span style={{ marginBottom: '5px' }}>Mold for</span>
                    {isImMaafcoEnabled ? (
                        renderAssociatedParts()
                    ) : (
                        <Tag className={styles.moldTag}>
                            <TruncatedText
                                maxLength={
                                    isOrderedStatusTable
                                        ? MAX_LENGTH
                                        : DEFAULT_TEXT_LENGTH
                                }
                            >
                                {revision?.modelFileName}
                            </TruncatedText>
                        </Tag>
                    )}
                </Box>
            )
        }
    }

    return (
        <Box direction="col" justifyContent="center">
            {showTsampleMsg && <span>T samples for</span>}
            <Box>
                {isTooling ? (
                    moldNameForTooling()
                ) : (
                    <Box className={styles.shippedLinkWrapper}>
                        {showMoldTag && (
                            <Tag className={styles.statusTag}>
                                {formattedVersion}
                            </Tag>
                        )}
                        <Box justifyContent="center">
                            <div>
                                {!showMoldTag ? (
                                    <>
                                        mold-
                                        {itemsId}
                                    </>
                                ) : (
                                    <Link
                                        name="revision-file-name"
                                        placement="order-detail-page"
                                        style={{
                                            padding: '0',
                                            margin: '0',
                                        }}
                                        onClick={onRevisionFileNamelinkClick}
                                    >
                                        <FileName
                                            className={styles.fileNameStyle}
                                            name={`${revision?.modelFileName}`}
                                            maxLength={
                                                isOrderedStatusTable
                                                    ? MAX_LENGTH
                                                    : MAX_FILE_NAME_LENGTH
                                            }
                                            style={{
                                                whiteSpace: 'normal',
                                            }}
                                        />
                                        <br />
                                    </Link>
                                )}
                            </div>
                        </Box>
                    </Box>
                )}
            </Box>
            {twoDdrawing && (
                <SafeDownloadLink
                    name="twoDrawing-attachment"
                    placement="inspections-ui"
                    downloadUrl={twoDdrawingDownloadUrl}
                    fileName={twoDdrawingFileName}
                    onClick={(evt: Event) => evt.stopPropagation()}
                    style={{ marginTop: '3px' }}
                >
                    <Tag className={styles.twoDdrawingTag}>
                        <TruncatedText
                            maxLength={
                                isOrderedStatusTable
                                    ? MAX_LENGTH
                                    : DEFAULT_TEXT_LENGTH
                            }
                        >
                            {twoDdrawingFileName}
                        </TruncatedText>
                    </Tag>
                </SafeDownloadLink>
            )}
            {showUnReleasedDfmTag && (
                <Box marginTop="4px">
                    <Tag
                        color="orange"
                        style={{
                            fontSize: '12px',
                            fontWeight: 400,
                        }}
                    >
                        Unreleased DFM
                    </Tag>
                </Box>
            )}
            {!isImToolingEnabled && showMoldIdTag && (
                <Box marginTop="4px">
                    <Tag className={styles.moldTag}>
                        <span>{erpMoldId}</span>
                    </Tag>
                </Box>
            )}
            {!isImToolingEnabled && hasDfmReports && (
                <Box marginTop="4px" paddingLeft="1px">
                    <Link
                        placement={placement}
                        data-test-target={`view-${placement}-dfm-report`}
                        name={`view-${placement}-dfm-report`}
                        style={{
                            fontSize: '14px',
                            fontWeight: 400,
                            color: getColor('warning'),
                        }}
                        underlined
                        onClick={onSeeDFMReportLinkClick}
                    >
                        View DFM report {pluralize('file', dfmReportsCount)}
                    </Link>
                </Box>
            )}
            {!isImToolingEnabled && hasDfmFeedback && (
                <Box marginTop="4px" paddingLeft="1px">
                    <Link
                        placement={placement}
                        data-test-target="dfm-available-link"
                        name={`view-${placement}-dfm-feedback`}
                        style={{
                            fontSize: '14px',
                            fontWeight: 400,
                            color: getColor('warning'),
                        }}
                        underlined
                        onClick={onDFMFeedbacklinkClick}
                    >
                        DFM available
                    </Link>
                </Box>
            )}
        </Box>
    )
}

export default DataTableFileName
